import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, Layers } from 'lucide-react';
import { PremiumQuoteCard } from '@/components/submission/ScoringResult';

const STYLE = {
  APPROVE: { bg: '#1D9E75', icon: CheckCircle, label: 'Approved' },
  CONDITIONS: { bg: '#00B4D8', icon: CheckCircle, label: 'Conditions' },
  REFER: { bg: '#E8A020', icon: AlertTriangle, label: 'Referred' },
  DECLINE: { bg: '#E24B4A', icon: XCircle, label: 'Declined' },
};
const fmt$ = (v) => `$${Math.round(v || 0).toLocaleString('en-AU')}`;

function Leg({ row, sub, onOpen }) {
  const ds = STYLE[row.decision] || STYLE.REFER;
  const Icon = ds.icon;
  const sr = sub?.scoring_result || {};
  const rules = sr.override_rules_triggered || [];
  return (
    <Card className="rounded-sm overflow-hidden border-0" data-testid={`combined-leg-${row.class_id}`}>
      <div className="p-4 text-white flex items-center gap-3" style={{ background: ds.bg }}>
        <Icon className="w-6 h-6" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-white/80">{row.class_name}</p>
          <p className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Sans'" }} data-testid={`combined-leg-${row.class_id}-decision`}>{ds.label}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-white/80">Payable</p>
          <p className="text-lg font-bold font-mono" data-testid={`combined-leg-${row.class_id}-payable`}>{fmt$(row.total_payable)}</p>
        </div>
      </div>
      <div className="p-4 bg-white space-y-3">
        {rules.length > 0 && (
          <div data-testid={`combined-leg-${row.class_id}-rules`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#B27516' }}>Rules fired — {rules.length}</p>
            {rules.map((r, i) => (
              <p key={i} className="text-xs flex items-start gap-1.5" style={{ color: '#0D1B2A' }}>
                <AlertTriangle className="w-3 h-3 mt-[2px] shrink-0" style={{ color: r.action === 'AUTO_DECLINE' ? '#E24B4A' : '#E8A020' }} />
                <span>{r.rule ? <span className="font-mono text-[10px] mr-1" style={{ color: '#5C6B7A' }}>{r.rule}</span> : null}{r.desc}</span>
              </p>
            ))}
          </div>
        )}
        {sr.marais_quote && <PremiumQuoteCard q={sr.marais_quote} />}
        {row.policy_id && <p className="text-xs font-mono" style={{ color: '#1D9E75' }} data-testid={`combined-leg-${row.class_id}-policy`}>Bound — {row.policy_id}</p>}
        <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => onOpen(row.submission_id)} data-testid={`combined-leg-${row.class_id}-open`}>Full quote details</Button>
      </div>
    </Card>
  );
}

export default function CombinedLiabilityResult({ result, onReset, onOpen }) {
  const { bundle, submissions } = result;
  const ds = STYLE[bundle.overall_decision] || STYLE.REFER;
  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="combined-liability-result">
      <Card className="rounded-sm overflow-hidden border-0">
        <div className="p-5 text-white flex items-center gap-3" style={{ background: ds.bg }}>
          <Layers className="w-7 h-7" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-white/80">Combined PI + Public Liability · {bundle.bundle_reference}</p>
            <h2 className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Sans'" }} data-testid="combined-overall-decision">{ds.label} — {bundle.business_name}</h2>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/80">Package payable</p>
            <p className="text-2xl font-bold font-mono" data-testid="combined-package-premium">{fmt$(bundle.package_premium)}</p>
            <p className="text-[10px] text-white/80">gross {fmt$(bundle.package_gross)} · {bundle.classes_quoted} of 2 legs quoted</p>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {bundle.classes.map(row => row.blocked
          ? (
            <Card key={row.class_id} className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#E24B4A' }} data-testid={`combined-leg-${row.class_id}-blocked`}>
              <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{row.class_id.replace(/_/g, ' ')}</p>
              <p className="text-xs mt-1" style={{ color: '#B03230' }}>{row.reason}</p>
            </Card>
          )
          : <Leg key={row.class_id} row={row} sub={submissions[row.class_id]} onOpen={onOpen} />)}
      </div>
      <div className="flex gap-3">
        <Button onClick={onReset} className="rounded-sm" style={{ background: '#1A4C7C' }} data-testid="combined-new-submission">New Submission</Button>
      </div>
    </div>
  );
}
